import React, { useState, useRef } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { getUploadUrl } from '../utils/config';
import {
  FaUser, FaIdCard, FaMapMarkerAlt, FaPhone,
  FaLock, FaSave, FaCamera
} from 'react-icons/fa';

const CATEGORIES = [
  'Farmer', 'Cultivator', 'Trader', 'Researcher', 'Student',
  'NGO Member', 'Government Employee', 'Ayurvedic Practitioner', 'Other'
];

export default function MemberEditModal({ member, isAdmin = false, onClose, onSuccess }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [form, setForm] = useState({
    name: member.name || '',
    father_name: member.father_name || '',
    mother_name: member.mother_name || '',
    spouse_name: member.spouse_name || '',
    full_address: member.full_address || '',
    village: member.village || '',
    post: member.post || '',
    hobli: member.hobli || '',
    taluk: member.taluk || '',
    district: member.district || '',
    pin: member.pin || '',
    mobile_phone: member.mobile_phone || '',
    email: member.email || '',
    aadhaar_no: member.aadhaar_no || '',
    pan: member.pan || '',
    voter_id: member.voter_id || '',
    ration_card_no: member.ration_card_no || '',
    driving_license_no: member.driving_license_no || '',
    passport_no: member.passport_no || '',
    category: member.category || '',
    status: member.status || 'approved',
  });

  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(member.photo_path ? getUploadUrl(member.photo_path) : null);
  const [signaturePreview, setSignaturePreview] = useState(member.signature_path ? getUploadUrl(member.signature_path) : null);
  const [loading, setLoading] = useState(false);

  const photoInputRef = useRef();
  const sigInputRef = useRef();

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignature(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Full Name is required');
    if (!form.mobile_phone.trim()) return toast.error('Mobile number is required');
    if (isAdmin && !form.aadhaar_no.trim()) return toast.error('Aadhaar number is required');

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        // Prevent regular user from tampering with aadhaar_no or non-superadmin changing status
        if (!isAdmin && k === 'aadhaar_no') return;
        if (!isSuperAdmin && k === 'status') return;
        formData.append(k, v !== null && v !== undefined ? v : '');
      });

      if (photo) formData.append('photo', photo);
      if (signature) formData.append('signature', signature);

      const targetId = member.id || member.member_id;
      const res = await API.put(`/members/${targetId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(res.data.message || 'Profile updated successfully!');
      if (onSuccess) onSuccess(res.data.member);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 760, width: '92%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a'
            }}>
              <FaUser size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#111827' }}>
                {isAdmin ? 'Edit Member Registration' : 'Edit My Profile'}
              </h2>
              <span style={{ fontFamily: 'monospace', color: '#16a34a', fontWeight: 600, fontSize: 13 }}>
                {member.member_id}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: '#f3f4f6', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16 }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          {/* Photos Upload Section */}
          <div style={{
            background: '#f9fafb', borderRadius: 12, padding: 16, marginBottom: 20,
            display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', border: '1px solid #f3f4f6'
          }}>
            {/* Member Photo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ position: 'relative' }}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #16a34a' }}
                  />
                ) : (
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%', background: '#e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af'
                  }}>
                    <FaUser size={28} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  style={{
                    position: 'absolute', bottom: -2, right: -2, width: 26, height: 26, borderRadius: '50%',
                    background: '#16a34a', color: 'white', border: '2px solid white', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                  title="Change Photo"
                >
                  <FaCamera size={11} />
                </button>
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Profile Photo</div>
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: '3px 8px', fontSize: 11, marginTop: 4 }}
                >
                  Change Photo
                </button>
              </div>
            </div>

            {/* Member Signature */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ position: 'relative' }}>
                {signaturePreview ? (
                  <img
                    src={signaturePreview}
                    alt="Signature Preview"
                    style={{ width: 100, height: 48, objectFit: 'contain', background: 'white', borderRadius: 6, border: '1px solid #d1d5db', padding: 2 }}
                  />
                ) : (
                  <div style={{
                    width: 100, height: 48, borderRadius: 6, background: '#f3f4f6', border: '1px dashed #d1d5db',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#9ca3af'
                  }}>
                    No Signature
                  </div>
                )}
                <input
                  type="file"
                  ref={sigInputRef}
                  accept="image/*"
                  onChange={handleSignatureChange}
                  style={{ display: 'none' }}
                />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Signature</div>
                <button
                  type="button"
                  onClick={() => sigInputRef.current?.click()}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: '3px 8px', fontSize: 11, marginTop: 4 }}
                >
                  Upload Signature
                </button>
              </div>
            </div>
          </div>

          {/* Section: Personal Info */}
          <div className="form-section-title" style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 12 }}>
            <FaUser size={12} style={{ marginRight: 6 }} /> Personal Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            <div className="form-group">
              <label className="form-label">Full Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Father&apos;s Name</label>
              <input className="form-input" name="father_name" value={form.father_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Mother&apos;s Name</label>
              <input className="form-input" name="mother_name" value={form.mother_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Spouse Name</label>
              <input className="form-input" name="spouse_name" value={form.spouse_name} onChange={handleChange} />
            </div>
          </div>

          {/* Section: Contact */}
          <div className="form-section-title" style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 12 }}>
            <FaPhone size={12} style={{ marginRight: 6 }} /> Contact Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            <div className="form-group">
              <label className="form-label">Mobile Number <span style={{ color: '#ef4444' }}>*</span></label>
              <input className="form-input" name="mobile_phone" value={form.mobile_phone} onChange={handleChange} maxLength={10} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" name="email" value={form.email} onChange={handleChange} />
            </div>
          </div>

          {/* Section: Address */}
          <div className="form-section-title" style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 12 }}>
            <FaMapMarkerAlt size={12} style={{ marginRight: 6 }} /> Address Details
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Full Address</label>
              <input className="form-input" name="full_address" value={form.full_address} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Village / Town</label>
              <input className="form-input" name="village" value={form.village} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Post</label>
              <input className="form-input" name="post" value={form.post} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Hobli</label>
              <input className="form-input" name="hobli" value={form.hobli} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Taluk</label>
              <input className="form-input" name="taluk" value={form.taluk} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">District</label>
              <input className="form-input" name="district" value={form.district} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">PIN Code</label>
              <input className="form-input" name="pin" value={form.pin} onChange={handleChange} maxLength={6} />
            </div>
          </div>

          {/* Section: Identity Documents */}
          <div className="form-section-title" style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 12 }}>
            <FaIdCard size={12} style={{ marginRight: 6 }} /> Identity Documents
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            {/* Aadhaar Number */}
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Aadhaar Number {isAdmin && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                {!isAdmin && (
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaLock size={10} color="#9ca3af" /> Fixed (Contact Admin to change)
                  </span>
                )}
              </div>
              <input
                className="form-input"
                name="aadhaar_no"
                value={form.aadhaar_no}
                onChange={handleChange}
                disabled={!isAdmin}
                style={!isAdmin ? { background: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' } : {}}
                maxLength={12}
                required={isAdmin}
              />
            </div>

            <div className="form-group">
              <label className="form-label">PAN Number</label>
              <input className="form-input" name="pan" value={form.pan} onChange={handleChange} style={{ textTransform: 'uppercase' }} maxLength={10} />
            </div>
            <div className="form-group">
              <label className="form-label">Voter ID</label>
              <input className="form-input" name="voter_id" value={form.voter_id} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Ration Card No</label>
              <input className="form-input" name="ration_card_no" value={form.ration_card_no} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Driving License No</label>
              <input className="form-input" name="driving_license_no" value={form.driving_license_no} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Passport No</label>
              <input className="form-input" name="passport_no" value={form.passport_no} onChange={handleChange} />
            </div>
          </div>

          {/* Section: Category & Admin Status */}
          <div style={{ display: 'grid', gridTemplateColumns: isSuperAdmin ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Member Category</label>
              <input
                type="text"
                id="modal-category-input"
                className="form-input"
                name="category"
                list="modal-category-options"
                value={form.category}
                onChange={handleChange}
                onClick={(e) => {
                  try { if (e.target.showPicker) e.target.showPicker(); } catch (_) {}
                }}
                placeholder="Type or select member category..."
                autoComplete="off"
              />
              <datalist id="modal-category-options">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </datalist>
            </div>

            {isSuperAdmin && (
              <div className="form-group">
                <label className="form-label">Approval</label>
                <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            )}
          </div>

          {/* Footer buttons inside form */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb'
          }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {loading ? (
                <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving...</>
              ) : (
                <><FaSave size={13} /> Save Changes</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
